/*
  <td-reviews-carousel-okendo>
  Fetches reviews from the Okendo API for the current product and renders a
  Swiper carousel. Handles loading / empty / error states. Swiper is loaded
  globally by layout/theme.liquid, so no asset include is needed here.

  All user-facing copy, the API host and the nav-button selectors come from
  data-* attributes set in snippets/td-reviews-carousel-okendo.liquid, so this
  asset holds no translatable strings and no vendor class names.

  5-star handling: the Okendo public API returns individual review objects
  (rating, reviewer, body), so highlighting is done here on real data rather
  than by scraping the rendered Okendo widget. Two independent controls:
  - qualityFilter 'five_star' shows only verified 5-star reviews.
  - prioritizeFiveStar (default on) stable-sorts 5-star reviews to the front of
    whichever set the filter produced, so the carousel leads with them but
    still backfills from 4-star rather than emptying out.
*/
if (!customElements.get('td-reviews-carousel-okendo')) {
  class TdReviewsCarouselOkendo extends HTMLElement {
    static SELECTORS = {
      carousel: '[data-td-carousel]',
      swiper: '[data-td-swiper]',
      viewAll: '[data-td-view-all]',
      nav: '.td-reviews-carousel-okendo__nav',
      navNext: '.swiper-square-arrow-next',
      navPrev: '.swiper-square-arrow-prev',
      status: '[data-td-status]',
      okendoWidget: '[data-oke-widget]',
    };
    static NAV_HIDDEN_CLASS = 'td-reviews-carousel-okendo__nav--hidden';
    static API_LIMIT = 25;
    static MAX_BODY_WORDS = 40;
    // Start the request a viewport ahead of the block so the reviews are ready
    // by the time the shopper scrolls to them, without competing with the
    // above-the-fold product media for bandwidth.
    static PREFETCH_MARGIN = '600px';

    constructor() {
      super();
      this.productId = this.dataset.productId || '';
      this.okendoUserId = this.dataset.okendoUserId || '';
      this.apiBase = (this.dataset.apiBase || '').replace(/\/+$/, '');
      this.settings = {};
      try {
        this.settings = JSON.parse(this.dataset.settings || '{}');
      } catch (e) {
        console.warn('TdReviewsCarouselOkendo: Invalid settings', e);
      }
      this.limit = Math.min(10, Math.max(3, this.settings.limit || 5));
      this.qualityFilter = this.settings.qualityFilter || 'balanced';
      // Default on: an unset value should still lead with 5-star reviews.
      this.prioritizeFiveStar = this.settings.prioritizeFiveStar !== false;
      this.swiper = null;
      this.observer = null;
      this.handleViewAllClick = this.handleViewAllClick.bind(this);
    }

    connectedCallback() {
      // Not configured (no product / no subscriber id / no API host) -> hide the
      // block entirely rather than showing shoppers a broken error state.
      if (!this.productId || !this.okendoUserId || !this.apiBase) {
        this.showState('empty');
        return;
      }
      this.attachViewAllListener();
      this.observeForFetch();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.swiper && typeof this.swiper.destroy === 'function') {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      if (this.viewAllCta) {
        this.viewAllCta.removeEventListener('click', this.handleViewAllClick);
        this.viewAllCta = null;
      }
      if (this.onWindowLoad) {
        window.removeEventListener('load', this.onWindowLoad);
        this.onWindowLoad = null;
      }
    }

    /**
     * Reads a translatable label passed down from the Liquid layer.
     * @param {string} key - camelCase dataset key, minus the `label` prefix
     * @param {string} [fallback] - used only if the attribute is missing
     * @returns {string}
     */
    label(key, fallback = '') {
      const value = this.dataset[`label${key.charAt(0).toUpperCase()}${key.slice(1)}`];
      return value === undefined || value === '' ? fallback : value;
    }

    /**
     * Substitutes the `%d` count placeholder in a label from the Liquid layer.
     * @param {string} template
     * @param {number} count
     * @returns {string}
     */
    formatCount(template, count) {
      return String(template).replace('%d', String(count));
    }

    /**
     * Defers the API request until the block is at or near the viewport, so a
     * busy product page does not pay for it during initial load.
     */
    observeForFetch() {
      if (typeof IntersectionObserver !== 'function') {
        this.fetchReviews();
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.observer = null;
        this.fetchReviews();
      }, { rootMargin: TdReviewsCarouselOkendo.PREFETCH_MARGIN });
      this.observer.observe(this);
    }

    /**
     * Attaches click listener to "View all reviews" link for smooth scroll to
     * the Okendo reviews widget section.
     */
    attachViewAllListener() {
      const wrapper = this.closest('.td-reviews-carousel-okendo');
      this.viewAllCta = wrapper?.querySelector(TdReviewsCarouselOkendo.SELECTORS.viewAll);
      if (!this.viewAllCta) return;
      this.viewAllCta.addEventListener('click', this.handleViewAllClick);
    }

    handleViewAllClick(e) {
      // Prefer the merchant-configured reference id (matches the theme's rating
      // block pattern); fall back to the Okendo widget element.
      const refId = this.settings.referenceId;
      const target = (refId && document.getElementById(refId)) ||
        document.querySelector(TdReviewsCarouselOkendo.SELECTORS.okendoWidget);
      if (target) {
        e.preventDefault();
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' });
      }
    }

    /**
     * Returns Okendo API orderBy param based on quality filter.
     * five_star asks the API for the highest ratings first so the fetched page
     * of API_LIMIT reviews actually contains enough 5-star ones to fill the
     * carousel; the others keep the original helpful/date ordering.
     */
    getOrderBy() {
      const map = {
        balanced: 'helpful%20desc',
        trust_focused: 'helpful%20desc',
        five_star: 'rating%20desc',
        all: 'date%20desc',
      };
      return map[this.qualityFilter] || map.balanced;
    }

    /**
     * Applies client-side quality filter to reviews array.
     * Returns filtered array (order preserved from API).
     */
    filterReviews(reviews) {
      if (!Array.isArray(reviews) || reviews.length === 0) return [];
      const f = this.qualityFilter;
      if (f === 'all') return reviews;
      return reviews.filter((r) => {
        const verified = !!r.reviewer?.isVerified;
        const rating = r.rating || 0;
        const incentivized = !!r.isIncentivized;
        if (f === 'balanced') return verified && rating >= 4 && !incentivized;
        if (f === 'trust_focused') return verified && rating >= 4;
        if (f === 'five_star') return verified && rating === 5;
        return true;
      });
    }

    /**
     * Stable-partitions 5-star reviews to the front, leaving the relative order
     * within each group untouched so the API's helpful/date ranking still
     * decides which 5-star review leads.
     * @param {Array} reviews
     * @returns {Array}
     */
    prioritizeFiveStarReviews(reviews) {
      if (!Array.isArray(reviews) || reviews.length < 2) return reviews;
      const top = [];
      const rest = [];
      reviews.forEach((r) => {
        if ((Number(r.rating) || 0) === 5) {
          top.push(r);
        } else {
          rest.push(r);
        }
      });
      return top.concat(rest);
    }

    /**
     * Shows a state of the component.
     * @param {string} state - State to show
     */
    showState(state) {
      this.dataset.tdState = state;
    }

    /**
     * Formats a date string to a human-readable date ago, using the labels
     * passed down from Liquid.
     * @param {string} dateStr - Date string to format
     * @returns {string} Formatted date ago
     */
    formatDateAgo(dateStr) {
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return '';
      const diffMs = Date.now() - date.getTime();
      const diffMonths = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30));
      const diffYears = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 365));
      if (diffYears >= 1) {
        return this.formatCount(this.label(diffYears > 1 ? 'yearsAgo' : 'yearAgo'), diffYears);
      }
      if (diffMonths >= 1) {
        return this.formatCount(this.label(diffMonths > 1 ? 'monthsAgo' : 'monthAgo'), diffMonths);
      }
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays >= 1) {
        return this.formatCount(this.label(diffDays > 1 ? 'daysAgo' : 'dayAgo'), diffDays);
      }
      return this.label('today');
    }

    /**
     * Escapes HTML characters in a string.
     * @param {string} str - String to escape
     * @returns {string} Escaped string
     */
    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    /**
     * Truncates text to a maximum number of words, appending ellipsis if truncated.
     * @param {string} str - Text to truncate
     * @param {number} maxWords - Maximum words to show
     */
    truncateWords(str, maxWords) {
      if (!str) return '';
      const words = str.trim().split(/\s+/);
      if (words.length <= maxWords) return str;
      return words.slice(0, maxWords).join(' ') + '…';
    }

    /**
     * Fetches reviews from Okendo API and renders the carousel.
     */
    async fetchReviews() {
      const orderBy = this.getOrderBy();
      const url = `${this.apiBase}/${this.okendoUserId}/products/shopify-${this.productId}/reviews?limit=${TdReviewsCarouselOkendo.API_LIMIT}&orderBy=${orderBy}`;
      try {
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        const raw = data?.reviews || [];
        const filtered = this.filterReviews(raw);
        const ordered = this.prioritizeFiveStar
          ? this.prioritizeFiveStarReviews(filtered)
          : filtered;
        const reviews = ordered.slice(0, this.limit);
        if (reviews.length === 0) {
          this.showState('empty');
          return;
        }
        this.renderCarousel(reviews);
      } catch (err) {
        console.warn('TdReviewsCarouselOkendo: Failed to fetch reviews', err);
        this.showState('error');
      }
    }

    /**
     * Renders the carousel with the reviews.
     * @param {Array} reviews - Array of reviews to render
     */
    renderCarousel(reviews) {
      this.showState('carousel');
      const carouselEl = this.querySelector(TdReviewsCarouselOkendo.SELECTORS.carousel);
      if (!carouselEl) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const opts = {
        centeredSlides: true,
        loop: reviews.length > 1,
        slidesPerView: 'auto',
        spaceBetween: 40,
        watchOverflow: true,
        breakpoints: { 769: { slidesPerView: 1.44, centeredSlides: false } },
      };
      if (this.settings.autoscroll && !reduceMotion) {
        // Pause on hover/focus so users can read (WCAG 2.2.2 Pause, Stop, Hide).
        opts.autoplay = {
          delay: (this.settings.autoscrollSpeed || 5) * 1000,
          pauseOnMouseEnter: true,
          disableOnInteraction: false,
        };
      }
      if (this.settings.showNav) {
        opts.navigation = {
          nextEl: this.dataset.navNext || TdReviewsCarouselOkendo.SELECTORS.navNext,
          prevEl: this.dataset.navPrev || TdReviewsCarouselOkendo.SELECTORS.navPrev,
        };
      }

      const starLabel = this.label('starRating');
      const verifiedLabel = this.escapeHtml(this.label('verified'));
      const slidesHtml = reviews.map((r) => {
        // Clamp to a valid 1-5 range so the aria-label and star fill stay sane
        // even if the API returns a missing/out-of-range rating.
        const rating = Math.min(5, Math.max(1, Number(r.rating) || 1));
        const title = this.escapeHtml(r.title || '');
        const body = this.truncateWords(this.escapeHtml(r.body || ''), TdReviewsCarouselOkendo.MAX_BODY_WORDS);
        const name = this.escapeHtml(r.reviewer?.displayName || this.label('anonymousReviewer'));
        const verified = r.reviewer?.isVerified;
        const dateStr = this.escapeHtml(this.formatDateAgo(r.dateCreated || ''));
        return `
          <div class="swiper-slide td-reviews-carousel-okendo__slide" data-td-slide data-td-rating="${rating}">
            <div class="td-reviews-carousel-okendo__slide-inner">
              <div class="td-reviews-carousel-okendo__card-top">
                <span class="td-reviews-carousel-okendo__stars" role="img" aria-label="${this.escapeHtml(this.formatCount(starLabel, rating))}" style="--rating: ${rating}"></span>
                <span class="td-reviews-carousel-okendo__date">${dateStr}</span>
              </div>
              ${title ? `<p class="td-reviews-carousel-okendo__title">${title}</p>` : ''}
              ${body ? `<p class="td-reviews-carousel-okendo__body">${body}</p>` : ''}
              <p class="td-reviews-carousel-okendo__reviewer-container">
                <span class="td-reviews-carousel-okendo__reviewer">${name}</span>
                ${verified ? `<span class="td-reviews-carousel-okendo__verified">${verifiedLabel}</span>` : ''}
              </p>
            </div>
          </div>
        `;
      }).join('');

      const carouselHeading = document.getElementById(this.getAttribute('aria-labelledby') || '');
      const carouselName = this.escapeHtml((carouselHeading?.textContent || '').trim());

      carouselEl.innerHTML = `
        <div class="swiper td-reviews-carousel-okendo__swiper" data-td-swiper data-ally-carousel="swiper"${carouselName ? ` aria-label="${carouselName}"` : ''}>
          <div class="swiper-wrapper td-swiper-wrapper">
            ${slidesHtml}
          </div>
        </div>
      `;

      const containerId = this.dataset.containerId || '';
      const wrapper = containerId ? document.getElementById(containerId) : this.closest('.td-reviews-carousel-okendo');
      const navWrap = wrapper?.querySelector(TdReviewsCarouselOkendo.SELECTORS.nav);
      if (navWrap && this.settings.showNav && reviews.length > 1) {
        navWrap.classList.remove(TdReviewsCarouselOkendo.NAV_HIDDEN_CLASS);
      }

      // Announce load to screen readers without reading every review aloud.
      const statusEl = this.querySelector(TdReviewsCarouselOkendo.SELECTORS.status);
      if (statusEl) {
        const n = reviews.length;
        statusEl.textContent = this.formatCount(this.label(n > 1 ? 'reviewsLoaded' : 'reviewLoaded'), n);
      }

      this.initSwiper(carouselEl, opts);
    }

    /**
     * Initializes the Swiper instance.
     * @param {Element} container - Container element
     * @param {Object} opts - Swiper options
     */
    initSwiper(container, opts) {
      // Swiper library requires its own class-based markup; select the instance root.
      const swiperEl = container.querySelector(TdReviewsCarouselOkendo.SELECTORS.swiper);
      if (!swiperEl) return;

      const init = () => {
        if (window.Swiper) {
          this.swiper = new window.Swiper(swiperEl, opts);
        }
      };

      if (document.readyState === 'complete') {
        init();
      } else {
        this.onWindowLoad = init;
        window.addEventListener('load', this.onWindowLoad, { once: true });
      }
    }
  }
  customElements.define('td-reviews-carousel-okendo', TdReviewsCarouselOkendo);
}
